import classNames from 'classnames';
import { ReadyIcon, Spinner } from 'components/shared/PriceNext';
import type { BootstrapColor } from 'models/BootstrapVars';
import { FC, useMemo } from 'react';
import { ExposedFormStatus } from 'state/forms/models';

export interface FormStatusIconProps {
  id?: string;
  status?: ExposedFormStatus;
  inProgressColor?: BootstrapColor;
  loadingColor?: BootstrapColor;
  executingColor?: BootstrapColor;
  className?: string;
  e2e?: string;
}

const commonClassName = 'icon icon-sm form-status-icon-component';

export const FormStatusIcon: FC<FormStatusIconProps> = ({
  id,
  status,
  inProgressColor = 'success',
  executingColor = 'info',
  loadingColor = 'muted',
  className,
  e2e,
}) => {
  const dataE2e = useMemo(() => e2e ?? 'form-status-icon', [e2e]);
  switch (status) {
    case 'NotReady':
      return (
        <i id={id} className={classNames('text-muted', commonClassName, className)} data-e2e={dataE2e}>
          filter_tilt_shift
        </i>
      );
    case 'Ready':
      return (
        <i id={id} className={classNames('text-info', commonClassName, className)} data-e2e={dataE2e}>
          <ReadyIcon dotColor="info" />
        </i>
      );
    case 'Loading':
    case 'Deleting':
    case 'Duplicating':
    case 'Patching':
    case 'Validating':
      return (
        <i id={id} className={classNames(`text-${loadingColor}`, commonClassName, className)} data-e2e={dataE2e}>
          {Spinner}
        </i>
      );
    case 'InProgress':
      return (
        <i id={id} className={classNames(`text-${inProgressColor}`, commonClassName, className)} data-e2e={dataE2e}>
          {Spinner}
        </i>
      );
    case 'Quoting':
    case 'QuotingAndAuthorizing':
    case 'QuotedPendingAuthorization':
    case 'AuthorizedPendingQuote':
    case 'Executing':
      return (
        <i id={id} className={classNames(`text-${executingColor}`, commonClassName, className)} data-e2e={dataE2e}>
          {Spinner}
        </i>
      );
    case 'Success':
    case 'Quoted':
    case 'Executed':
      return (
        <i id={id} className={classNames('text-success', commonClassName, className)} data-e2e={dataE2e}>
          check_circle
        </i>
      );
    case 'Expired':
      return (
        <i id={id} className={classNames('text-warning', commonClassName, 'icon-md', className)} data-e2e={dataE2e}>
          schedule
        </i>
      );
    case 'UnknownError':
      return (
        <i className={classNames('text-danger', commonClassName, className)} data-e2e={dataE2e}>
          help_outline
        </i>
      );
    case 'Rejected':
    case 'Timeout':
    case 'QuoteTimeout':
    case 'TradeAuthorizationTimeout':
    case 'TradeTimeout':
      return (
        <i id={id} className={classNames('text-danger', commonClassName, className)} data-e2e={dataE2e}>
          block
        </i>
      );
    case 'ActionFailed':
    case 'Warning':
      return (
        <i id={id} className={classNames('text-warning', commonClassName, className)} data-e2e={dataE2e}>
          block
        </i>
      );
    default:
      return (
        <i id={id} className={classNames('text-muted', commonClassName, className)} data-e2e={dataE2e}>
          help_outline
        </i>
      );
  }
};
